import { SwishPaymentRequestForm } from "../Types/PaymentFormTypes";
import { CheckoutOrderRequest, CheckoutOrderResponse } from "../Types/CartTypes";
import { PromoCodeVerification } from "../Types/PromoCodeTypes";
import { AdminInvoice } from "../Types/AdminOrderTypes";

import { apiGet, apiPost, apiPublicPost, apiPublicRawPost, apiRawPost } from "./apiClient";

const apiBase = process.env.REACT_APP_API_URL;
const swishCallbackUrl = process.env.REACT_APP_SWISH_CALLBACK_URL;

const readErrorMessage = async (response: Response, fallback: string) => {
  const text = await response.text();

  if (!text.trim()) return fallback;

  try {
    const parsed = JSON.parse(text);

    if (typeof parsed === 'string') return parsed;
    if (Array.isArray(parsed)) {
      const first = parsed[0];
      return first?.errorMessage ?? first?.error ?? first?.message ?? text;
    }

    return parsed.errorMessage ?? parsed.error ?? parsed.message ?? text;
  } catch {
    return text;
  }
};

/* ============================================================
   PUBLIC – Verify Promo Code
============================================================ */
async function SendPromoCodeVerification(
  request: string
): Promise<{ message: string; promoCode?: PromoCodeVerification }> {
  try {
    const response = await apiPublicRawPost(
      "/PromoCode/verifypromo",
      request
    );

    const result = await response.json().catch(() => ({}));
    return result;
  } catch (err) {
    console.error("Error verifying promo code", err);
    return { message: "Kunde inte verifiera kampanjkoden.", promoCode: undefined };
  }
}

/* ============================================================
   PROTECTED – Create Swish Payment
============================================================ */

const CreateCheckoutSession = async (request: CheckoutOrderRequest): Promise<CheckoutOrderResponse> => {
  return await apiPublicPost<CheckoutOrderResponse>("/Checkout/session", request);
};

type CreateSubscriptionResponse = {
  subscriptionId: string;
  clientSecret: string;
};

const CreateSubscription = async (request: { email: string; priceId: string; applicationUserId?: string; planKey?: string; membershipSnapshotJson?: string }): Promise<CreateSubscriptionResponse> => {
  return await apiPublicPost<CreateSubscriptionResponse>("/api/stripe/subscription", request);
};

const SendSwishPaymentRequest = async (form: SwishPaymentRequestForm) => {
  const callbackUrl = form.callbackUrl ?? swishCallbackUrl ?? (apiBase ? `${apiBase}/api/Swish/payment-callback` : "/api/Swish/payment-callback");
  const response = await apiPublicRawPost("/api/Swish/payments", {
    ...form,
    callbackUrl,
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Kunde inte starta Swishbetalningen.'));
  }

  const result = await response.json().catch(() => ({}));
  console.log("RESULT FROM SWISH PAYMENT REQUEST:", result);
  return result?.originalPaymentReference ?? null;
};

const SendAdminTestSwishPaymentRequest = async (form: { payerAlias: string; amount?: number; message?: string }) => {
  const response = await apiRawPost("/api/Swish/admin-test-payment", {
    payerAlias: form.payerAlias,
    amount: form.amount ?? 1,
    message: form.message ?? "Admin test payment",
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Kunde inte starta testbetalningen.'));
  }

  const result = await response.json().catch(() => ({}));
  return result?.originalPaymentReference ?? null;
};

const GetAdminInvoices = async (): Promise<AdminInvoice[]> => {
  return await apiGet<AdminInvoice[]>("/api/invoices/admin");
};

const MarkInvoicePaid = async (invoiceId: string): Promise<void> => {
  await apiPost(`/api/invoices/${invoiceId}/mark-paid`);
};

export const PaymentService = {
  SendPromoCodeVerification,
  CreateCheckoutSession,
  CreateSubscription,
  SendSwishPaymentRequest,
  SendAdminTestSwishPaymentRequest,
  GetAdminInvoices,
  MarkInvoicePaid,
};
